/**
 * The preview as the page holds it: the Worker's point, its range rows streaming after, and the point before the last edit
 * (for the was → now ghost), with the CRO watch of the latest point (kept until the next arrives) and the trace of what each decision did (cleared with each run). A newer edit supersedes the run in flight; a crash shows "Preview unavailable" until Retry.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { DraftView } from "@bsim/contracts";
import type { PreviewInput, PreviewResult, PreviewTrace } from "@bsim/domain";
import { PreviewSuperseded, type PreviewWatch, type PreviewWorkerClient } from "../preview-worker";
import { draftDecisionSet } from "./draftSet";

export type PreviewState =
  | { readonly status: "computing"; readonly previous: PreviewResult | null }
  | { readonly status: "ready"; readonly result: PreviewResult; readonly previous: PreviewResult | null; readonly at: Date }
  | { readonly status: "error" };

/** The probed decision's step each way for the run on screen: the run's point with `nextStep` and `yearEndDrivers` filled (ADR-0091). */
export interface PreviewSteps { readonly decisionId: string; readonly up?: PreviewResult; readonly down?: PreviewResult }

export function usePreview(worker: PreviewWorkerClient, input: PreviewInput | undefined, draft: DraftView | null, valueLegs = false, probe: string | null = null): { readonly state: PreviewState; readonly watch: PreviewWatch | null; readonly trace: PreviewTrace | null; readonly steps: PreviewSteps | null; /** The input the `ready` result was run on: a result is stale while the round's context is a different one. */ readonly basis: PreviewInput | undefined; readonly retry: () => void } {
  const [state, setState] = useState<PreviewState>({ status: "computing", previous: null });
  const [watch, setWatch] = useState<PreviewWatch | null>(null);
  const [trace, setTrace] = useState<PreviewTrace | null>(null);
  const [steps, setSteps] = useState<PreviewSteps | null>(null);
  const [basis, setBasis] = useState<PreviewInput | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const lastPoint = useRef<PreviewResult | null>(null);
  const lastRound = useRef<string | undefined>(undefined);
  const inputKey = useRef<PreviewInput | undefined>(undefined);
  const decisionsKey = JSON.stringify(draft?.decisions);

  useEffect(() => { worker.onRanges((r) => setState((s) => (s.status === "ready" ? { ...s, result: r } : s))); }, [worker]);
  useEffect(() => { worker.onWatch(setWatch); }, [worker]);
  useEffect(() => { worker.onTrace(setTrace); }, [worker]);
  useEffect(() => { worker.onNextStep(({ direction, decisionId, result }) => setSteps((s) => ({ ...(s?.decisionId === decisionId ? s : { decisionId }), [direction === 1 ? "up" : "down"]: result }))); }, [worker]);
  // The decision to step follows the panel on screen; the Worker steps it after each run and, when the run is finished, now.
  useEffect(() => { setSteps(null); worker.probe(probe); return () => worker.probe(null); }, [worker, probe]);

  useEffect(() => {
    if (input === undefined || draft === null) return;
    // The point before an edit is the baseline for the ghost; a new input (gates lifted) starts over.
    if (lastRound.current !== draft.roundId) { lastPoint.current = null; lastRound.current = draft.roundId; } // another round's point is never held on this round's screen
    const previous = inputKey.current === input ? lastPoint.current : null;
    inputKey.current = input;
    // What the panel holds on screen while this run is out is the last point whatever the input (a card answered, a new context): figures never go blank; only the ghost baseline needs the same input.
    setState({ status: "computing", previous: lastPoint.current });
    // The trace belongs to one run: it is dropped with the point it explained and arrives again after the next.
    setTrace(null);
    setSteps(null);
    worker.run(input, draftDecisionSet(input, draft), valueLegs).then((result) => {
      lastPoint.current = result;
      setBasis(input);
      setState({ status: "ready", result, previous, at: new Date() });
    }, (e: unknown) => { if (!(e instanceof PreviewSuperseded)) setState({ status: "error" }); });
    // Only the decisions and the input drive a run; the rest of the draft (rationales, thesis text) never moves the preview.
  }, [worker, input, decisionsKey, attempt, valueLegs]);

  useEffect(() => () => worker.cancel(), [worker]);
  return { state, watch, trace, steps, basis, retry: useCallback(() => setAttempt((n) => n + 1), []) };
}
